import React, { useEffect, useRef } from "react";
import { Box, Flex, Button } from "@chakra-ui/react";

/**
 * Lightweight dependency-free rich-text editor built on contentEditable.
 *
 * Edits HTML in place (so existing inline-styled email markup is preserved) and
 * reports the resulting innerHTML via onChange. The DOM is only re-synced from
 * `value` when it changes externally (seed/reset), never on the user's own
 * keystrokes, so the caret is preserved while typing.
 *
 * Toolbar buttons use document.execCommand. They are deprecated but still
 * supported in every browser and keep us free of a heavy editor dependency. No
 * link button on purpose (it would require a browser prompt, which the portal
 * forbids; the email's apply link is handled by a token).
 */

const TOOLBAR = [
  { cmd: "bold", label: "B", title: "Bold", fontWeight: "700" },
  { cmd: "italic", label: "I", title: "Italic", fontStyle: "italic" },
  { cmd: "underline", label: "U", title: "Underline", textDecoration: "underline" },
  { cmd: "insertUnorderedList", label: "• List", title: "Bullet list" },
  { cmd: "insertOrderedList", label: "1. List", title: "Numbered list" },
  { cmd: "formatBlock", arg: "H2", label: "Heading", title: "Heading" },
  { cmd: "formatBlock", arg: "P", label: "Text", title: "Normal text" },
  { cmd: "removeFormat", label: "Clear", title: "Clear formatting" },
];

const RichTextEditor = ({
  value,
  onChange,
  ariaLabel = "Editor",
  minHeight = "320px",
  "data-testid": dataTestid = "rich-text-editor",
  ...rest
}) => {
  const ref = useRef(null);

  // Sync DOM only when the value changed from outside (seed/reset). When the
  // change came from our own onInput, value === innerHTML already, so we skip it.
  useEffect(() => {
    if (ref.current && (value || "") !== ref.current.innerHTML) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const handleInput = () => {
    if (onChange && ref.current) onChange(ref.current.innerHTML);
  };

  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
    if (ref.current) {
      ref.current.focus();
      handleInput();
    }
  };

  return (
    <Box borderWidth="1px" borderColor="portal.line" borderRadius="10px" overflow="hidden" {...rest}>
      <Flex
        gap="2px"
        flexWrap="wrap"
        p="6px"
        bg="portal.soft"
        borderBottomWidth="1px"
        borderColor="portal.line"
      >
        {TOOLBAR.map((b) => (
          <Button
            key={b.label}
            size="xs"
            variant="ghost"
            title={b.title}
            aria-label={b.title}
            // Keep focus/selection in the editor when a toolbar button is pressed.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec(b.cmd, b.arg)}
            fontWeight={b.fontWeight}
            fontStyle={b.fontStyle}
            sx={b.textDecoration ? { textDecoration: b.textDecoration } : undefined}
          >
            {b.label}
          </Button>
        ))}
      </Flex>
      <Box
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        role="textbox"
        aria-label={ariaLabel}
        aria-multiline="true"
        data-testid={dataTestid}
        minH={minHeight}
        maxH="520px"
        overflowY="auto"
        p="14px"
        fontSize="13px"
        fontFamily="Arial, sans-serif"
        color="#333"
        bg="white"
        sx={{
          "&:focus": { outline: "none" },
          "& h1, & h2": { fontSize: "18px", fontWeight: "700", margin: "0 0 8px 0" },
          "& p": { margin: "0 0 8px 0" },
          "& ul, & ol": { paddingLeft: "20px", margin: "0 0 8px 0" },
        }}
      />
    </Box>
  );
};

export default RichTextEditor;
